import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { RBACService } from 'src/app/shared/rbac.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { SsspTabsDialogComponent } from 'src/app/sssp/sssp-tabs/sssp-tabs-dialog.component';
import { ApiDataSource } from 'src/app/utils/api-data-source';

@Component({
  selector: 'app-client-sites',
  templateUrl: './client-sites.component.html',
  styleUrls: ['./client-sites.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientSitesComponent implements OnInit {
  listColumns: string[] = [];
  listDataSource = new ClientSitesDataSource(this.app, this.api);

  // RBAC permission flags: resolved once in ngOnInit().
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private api: ApiRequestService,
    private app: AppService,
    public rbac: RBACService,
    private router: Router,
    public utils: UtilsService
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CLIENT_SITES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CLIENT_SITES,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CLIENT_SITES,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access client sites.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build listColumns dynamically.
    this.listColumns = ['name', 'location', 'owner'];
    if (this.canView || this.canEdit) {
      this.listColumns.push('actions');
    }

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.listDataSource.getData();
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.listDataSource.limit = paginator['pageSize'];
      this.listDataSource.offset = paginator['pageIndex'];
      this.listDataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange
      .pipe(
        tap((sorter) => {
          this.listDataSource.order_by = sorter['active'];
          this.listDataSource.order = sorter['direction'];
          this.listDataSource.getData();
        })
      )
      .subscribe();
  }

  onCreateSSSP(site_id: number) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage safety plans for client sites.'
      );
      return;
    }

    // Make a post request to create a new Safety Plan.
    // Reload the list to show the latest changes.
    this.api
      .makeRequest('post', 'v1/sssp', {
        site_id: site_id,
        contractor_id: this.app.account.id
      })
      .then((response: any) => {
        // This means the request was successful.
        // Open it in a dialog.
        this.onManageSSSP(response.sssp_hash);
        // Reload the list in the background.
        this.listDataSource.getData();
      })
      .catch((error) => {
        const safety_plan_term = this.utils.getLangTerm(
          'safety-plans.singular',
          'Safety Plan'
        );
        this.utils.showToast(
          `The ${safety_plan_term} was created successfully.`
        );
      });
  }

  onManageSSSP(sssp_hash: string) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage safety plans for client sites.'
      );
      return;
    }

    // Open the Safety Plan manager using a dialog window.
    this.utils.showComponentDialog(
      SsspTabsDialogComponent,
      {
        sssp_hash: sssp_hash
      },
      {
        width: '1200px'
      }
    );
  }

  onFilterByParentSite(parent_site_id: number) {
    this.listDataSource.parent_site_id = parent_site_id;
    this.listDataSource.getData(true);
  }

  onResetList() {
    this.listDataSource.parent_site_id = null;
    this.listDataSource.getData(true);
  }

  protected readonly RBACService = RBACService;
}

export class ClientSitesDataSource extends ApiDataSource {
  order_by = 'name';
  order = 'asc';

  // The parent site id to filter by.
  parent_site_id: number;

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/user/accessible-sites', resetOffset, {
      traverse_recursively: true,
      include_employer_sites: true,
      order: this.order,
      order_by: this.order_by,
      parent_site_id: this.parent_site_id
    });
  }
}
